import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Printer, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';
import { openPrintWindow } from '@/lib/letterhead';

const STILI_ASSENZA = {
  R: 'bg-slate-100 text-slate-500',
  FERIE: 'bg-brand-soft text-brand-dark',
  MALATTIA: 'bg-red-100 text-red-600',
  ROL: 'bg-amber-100 text-amber-700',
  PERMESSO: 'bg-sky-100 text-sky-700',
};

const breve = (fascia) => fascia.replace(/\.00/g, '').replace('/', '-');

export default function TurniTab() {
  const { hasRole } = useAuth();
  const puoModificare = hasRole('coordinatore', 'amministrazione');
  const [mese, setMese] = useState(new Date().toISOString().slice(0, 7));
  const [ruolo, setRuolo] = useState('tutti');
  const [dati, setDati] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cella, setCella] = useState(null);

  const fetchDati = useCallback(async () => {
    setLoading(true);
    try { setDati((await api.get('/moduli/turni', { params: { mese, ruolo } })).data); }
    catch { toast.error('Errore caricamento turni'); }
    setLoading(false);
  }, [mese, ruolo]);

  useEffect(() => { fetchDati(); }, [fetchDati]);

  const salva = async (payload) => {
    try {
      await api.put('/moduli/turni', { operatore: cella.riga.nome, user_id: cella.riga.con_account ? cella.riga.id : '', ruolo: cella.riga.ruolo, data: cella.giorno, ...payload });
      setCella(null);
      fetchDati();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore salvataggio turno'); }
  };

  const stampa = () => {
    if (!dati) return;
    let html = `<table><thead><tr><th>Operatore</th>${dati.giorni.map(g => `<th style="text-align:center">${g.slice(8)}</th>`).join('')}<th>Ore</th><th>R</th></tr></thead><tbody>`;
    dati.righe.forEach(r => {
      html += `<tr><td>${r.nome}</td>${dati.giorni.map(g => {
        const v = r.valori[g];
        return `<td style="text-align:center;font-size:7px">${v.tipo === 'lavoro' ? (v.fascia ? breve(v.fascia) : v.ore) : (v.tipo || '')}</td>`;
      }).join('')}<td style="text-align:center">${r.ore_totali}</td><td style="text-align:center">${r.riposi}</td></tr>`;
    });
    html += `<tr><td><strong>Ore coperte</strong></td>${dati.giorni.map(g => `<td style="text-align:center"><strong>${dati.copertura[g] || 0}</strong></td>`).join('')}<td></td><td></td></tr></tbody></table>`;
    openPrintWindow('Schema Turni Personale', html, { subtitle: `Mese ${mese} · ${dati.ore_mese} ore totali` });
  };

  if (loading && !dati) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-brand" /></div>;

  return (
    <div className="space-y-4" data-testid="turni-tab">
      <div className="flex flex-wrap items-center gap-2">
        <Input type="month" value={mese} onChange={e => setMese(e.target.value)} className="w-40 h-9" data-testid="turni-mese" />
        <Select value={ruolo} onValueChange={setRuolo}>
          <SelectTrigger className="w-44 h-9" data-testid="turni-ruolo"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="tutti">Tutti i ruoli</SelectItem>
            <SelectItem value="infermiere">Infermieri</SelectItem>
            <SelectItem value="oss">OSS</SelectItem>
            <SelectItem value="medico">Medici</SelectItem>
          </SelectContent>
        </Select>
        <Badge className="bg-brand-soft text-brand-dark">{dati.ore_mese} ore nel mese</Badge>
        <Button size="sm" variant="outline" onClick={stampa} className="h-9" data-testid="turni-print">
          <Printer className="w-4 h-4 mr-1" />Stampa
        </Button>
      </div>

      <Card>
        <CardContent className="p-0 overflow-auto max-h-[65vh]">
          <table className="w-full text-[10px] border-collapse">
            <thead className="sticky top-0 bg-white z-10">
              <tr>
                <th className="text-left px-2 py-2 border-b border-slate-200 sticky left-0 bg-white min-w-[150px] text-[11px]">Operatore</th>
                {dati.giorni.map(g => <th key={g} className="px-0 py-2 border-b border-slate-200 text-center w-12 text-slate-400">{g.slice(8)}</th>)}
                <th className="px-2 py-2 border-b border-slate-200 text-center">Ore</th>
                <th className="px-1 py-2 border-b border-slate-200 text-center">R</th>
                <th className="px-1 py-2 border-b border-slate-200 text-center">F</th>
              </tr>
            </thead>
            <tbody>
              {dati.righe.map(r => (
                <tr key={r.id} className="hover:bg-slate-50">
                  <td className="px-2 py-1 border-b border-slate-100 sticky left-0 bg-white font-medium text-slate-700 whitespace-nowrap text-[11px]">
                    {r.nome}{!r.con_account && <span className="text-slate-300"> ·</span>}
                  </td>
                  {dati.giorni.map(g => {
                    const v = r.valori[g];
                    const assenza = v.tipo && v.tipo !== 'lavoro';
                    return (
                      <td key={g} className="border-b border-slate-100 p-0.5">
                        <button onClick={() => puoModificare ? setCella({ riga: r, giorno: g, valore: v }) : toast.error('Solo il coordinatore puo modificare i turni')}
                          className={`w-11 h-7 rounded border border-slate-200 text-[9px] leading-tight transition-colors hover:ring-1 hover:ring-brand ${assenza ? STILI_ASSENZA[v.tipo] : v.ore ? 'bg-white text-slate-700 font-semibold' : 'bg-white text-slate-300'}`}
                          data-testid={`turno-cell-${r.id}-${g}`}>
                          {assenza ? (v.tipo === 'MALATTIA' ? 'MAL' : v.tipo) : v.fascia ? breve(v.fascia) : (v.ore || '')}
                        </button>
                      </td>
                    );
                  })}
                  <td className="px-2 py-1 border-b border-slate-100 text-center font-semibold text-brand-dark">{r.ore_totali}</td>
                  <td className="px-1 py-1 border-b border-slate-100 text-center text-slate-500">{r.riposi}</td>
                  <td className="px-1 py-1 border-b border-slate-100 text-center text-slate-500">{r.ferie}</td>
                </tr>
              ))}
              <tr className="bg-slate-50 font-semibold">
                <td className="px-2 py-2 sticky left-0 bg-slate-50 text-[11px]">Ore coperte</td>
                {dati.giorni.map(g => <td key={g} className="text-center text-brand-dark">{dati.copertura[g] || 0}</td>)}
                <td colSpan={3} />
              </tr>
            </tbody>
          </table>
        </CardContent>
      </Card>
      <p className="text-[11px] text-slate-400">Tocca una cella per assegnare la fascia orario o l'assenza. Le ore si calcolano dalla fascia.</p>

      <Dialog open={!!cella} onOpenChange={() => setCella(null)}>
        <DialogContent className="max-w-md" data-testid="turno-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm">
              {cella?.riga.nome} · {cella && new Date(cella.giorno).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <p className="text-[11px] text-slate-500 mb-1">Fascia orario</p>
              <div className="grid grid-cols-3 gap-1.5">
                {dati.fasce.map(f => (
                  <Button key={f} size="sm" variant="outline" className="h-8 text-[11px]"
                    onClick={() => salva({ fascia: f, tipo: 'lavoro' })} data-testid={`fascia-${f}`}>{f}</Button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-[11px] text-slate-500 mb-1">Assenza</p>
              <div className="flex flex-wrap gap-1.5">
                {dati.tipi_assenza.map(t => (
                  <Button key={t} size="sm" variant="outline" className="h-8 text-[11px]"
                    onClick={() => salva({ tipo: t })} data-testid={`assenza-${t}`}>{t}</Button>
                ))}
                <Button size="sm" variant="ghost" className="h-8 text-[11px] text-red-500"
                  onClick={() => salva({ tipo: 'lavoro', fascia: '', ore: 0 })} data-testid="turno-cancella">Cancella</Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
